import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Chip,
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import TopicIcon from '@mui/icons-material/Topic';
import ExploreIcon from '@mui/icons-material/Explore';

// Extract themes from conversations
const extractThemes = (conversations) => {
  const themeCount = {};
  
  conversations.forEach(conversation => {
    const content = (conversation.content || '').toLowerCase();
    
    // Simple theme detection based on common patterns
    const themes = [];
    
    // Technology themes
    if (/\b(code|programming|software|development|javascript|python|react|api|database|algorithm)\b/.test(content)) {
      themes.push('Technology');
    }
    
    // Business themes
    if (/\b(business|marketing|strategy|sales|revenue|profit|customer|market)\b/.test(content)) {
      themes.push('Business');
    }
    
    // Education themes
    if (/\b(learn|education|study|course|tutorial|lesson|teaching|knowledge)\b/.test(content)) {
      themes.push('Education');
    }
    
    // Health themes
    if (/\b(health|medical|doctor|medicine|treatment|symptoms|wellness)\b/.test(content)) {
      themes.push('Health');
    }
    
    // Science themes
    if (/\b(science|research|experiment|data|analysis|theory|hypothesis)\b/.test(content)) {
      themes.push('Science');
    }
    
    // Creative themes
    if (/\b(art|design|creative|music|writing|story|novel|painting)\b/.test(content)) {
      themes.push('Creative');
    }
    
    // Travel themes
    if (/\b(travel|trip|vacation|hotel|flight|destination|tourism)\b/.test(content)) {
      themes.push('Travel');
    }
    
    // Food themes
    if (/\b(food|recipe|cooking|restaurant|meal|ingredient|cuisine)\b/.test(content)) {
      themes.push('Food');
    }
    
    // If no specific theme detected, categorize as General
    if (themes.length === 0) {
      themes.push('General');
    }
    
    themes.forEach(theme => {
      themeCount[theme] = (themeCount[theme] || 0) + 1;
    });
  });
  
  // Sort by frequency
  return Object.entries(themeCount)
    .sort(([,a], [,b]) => b - a)
    .map(([theme, count]) => ({ theme, count }));
};

// Search panel component
const SearchPanel = ({ searchQuery, onSearch }) => {
  const [keyword, setKeyword] = useState(searchQuery || '');

  const handleKeywordChange = (event) => {
    const value = event.target.value;
    setKeyword(value);
    onSearch(value);
  };

  const clearSearch = () => {
    setKeyword('');
    onSearch('');
  };

  return (
    <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Discover Content
      </Typography>
      
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Search Topics"
            value={keyword}
            onChange={handleKeywordChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: keyword && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear search"
                    onClick={clearSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Button
            variant="outlined"
            size="small"
            onClick={clearSearch}
            startIcon={<ClearIcon />}
            fullWidth
          >
            Clear Search
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

// Theme list component
const ThemeList = ({ themes, onThemeClick, searchQuery }) => {
  const getThemeColor = (theme) => {
    const colors = {
      'Technology': 'primary',
      'Business': 'secondary',
      'Education': 'success',
      'Health': 'error',
      'Science': 'info',
      'Creative': 'warning',
      'Travel': 'primary',
      'Food': 'secondary',
      'General': 'default'
    };
    return colors[theme] || 'default';
  };

  return (
    <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Content Themes
      </Typography>
      
      {themes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No themes found
        </Typography>
      ) : (
        <List dense>
          {themes.map(({ theme, count }, index) => (
            <ListItem key={theme} disablePadding>
              <ListItemButton 
                onClick={() => onThemeClick(theme)}
                selected={searchQuery === theme.toLowerCase()}
              >
                <TopicIcon sx={{ mr: 1, fontSize: 16 }} />
                <ListItemText 
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{theme}</Typography>
                      <Chip 
                        label={count} 
                        size="small" 
                        color={getThemeColor(theme)}
                        variant="outlined"
                      />
                    </Stack>
                  }
                  secondary={`${count} conversation${count !== 1 ? 's' : ''}`}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

// Discovery results component
const DiscoveryResults = ({ conversations, themes, searchQuery, onConversationClick, onNavItemClick }) => {
  // Filter conversations based on search query or theme
  const getFilteredConversations = () => {
    if (!searchQuery || !searchQuery.trim()) return [];
    
    const query = searchQuery.trim().toLowerCase();
    
    return conversations.filter(conversation => {
      const content = (conversation.content || '').toLowerCase();
      
      // Check if it matches the search query
      const keywords = query.split(/\s+/);
      const matchesKeywords = keywords.some(keyword => 
        content.includes(keyword)
      );
      
      // Check if it matches a theme
      const matchesTheme = themes.some(theme => 
        theme.theme.toLowerCase() === query && 
        content.includes(query)
      );
      
      return matchesKeywords || matchesTheme;
    }).sort((a, b) => 
      new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
    );
  };

  const filteredConversations = getFilteredConversations();

  if (!searchQuery || !searchQuery.trim()) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <ExploreIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Discover Your Conversations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search for topics or select a theme to explore your conversations
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Discovery Results for "{searchQuery}"
      </Typography>
      
      {filteredConversations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No conversations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try different topics or check the timeline view
          </Typography>
          <Button 
            variant="outlined" 
            sx={{ mt: 2 }}
            onClick={() => onNavItemClick('timeline')}
          >
            View Timeline
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''} discovered
          </Typography>
          
          {filteredConversations.map((conversation, index) => (
            <Card key={conversation.id || `discovery-${index}`} sx={{ mb: 2 }} elevation={2}>
              <CardActionArea onClick={() => onConversationClick(conversation)}>
                <CardContent>
                  <Typography variant="h6" component="div" gutterBottom>
                    {conversation.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {conversation.content 
                      ? conversation.content.substring(0, 150) + (conversation.content.length > 150 ? '...' : '')
                      : 'No content'
                    }
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip 
                      label={conversation.sender || 'unknown'} 
                      size="small" 
                      color={conversation.sender === 'user' ? 'primary' : 'secondary'}
                      sx={{ mb: 0.5 }}
                    />
                    {conversation.imageData && (
                      <Chip 
                        label="Has Image" 
                        size="small" 
                        color="info"
                        sx={{ mb: 0.5 }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Main Discovery View component
const EnDiscoveryView = ({ 
  conversations, 
  themes,
  onConversationClick, 
  searchQuery, 
  onSearch,
  onNavItemClick
}) => {
  const extractedThemes = extractThemes(conversations);

  const handleThemeClick = (theme) => {
    onSearch(theme.toLowerCase());
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Discovery
      </Typography>
      
      <SearchPanel 
        searchQuery={searchQuery}
        onSearch={onSearch}
      />
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ThemeList 
            themes={extractedThemes}
            onThemeClick={handleThemeClick}
            searchQuery={searchQuery}
          />
        </Grid>
        
        <Grid item xs={12} md={8}>
          <DiscoveryResults 
            conversations={conversations}
            themes={extractedThemes}
            searchQuery={searchQuery}
            onConversationClick={onConversationClick}
            onNavItemClick={onNavItemClick}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnDiscoveryView; 